import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../config/api';
import CustomNavbar from '../CustomPermissions/CustomNavbar';
import './TaskManagement.css';

function TaskManagement() {
  const [activeTab, setActiveTab] = useState('leaders'); // 'leaders' or 'docladder'
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [deleteConfirmTask, setDeleteConfirmTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [taskForm, setTaskForm] = useState({
    name: '',
    target: '',
    points: '',
    leader: '' // This will be set when user is selected
  });

  const accessToken = localStorage.getItem('access_token');

  useEffect(() => {
    fetchUsers();
  }, [activeTab]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const endpoint = activeTab === 'leaders' 
        ? '/LeadersTable/all_leaders_with_tasks/'
        : '/Docladder/all_members_with_tasks/';
      
      const response = await axios.get(getApiUrl(endpoint), {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      
      setUsers(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users. Make sure you are logged in as a super admin.');
      setLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    if (activeTab === 'leaders') {
      setSelectedUser(user.leader);
      setTasks(user.tasks);
      setTaskForm({ ...taskForm, leader: user.leader.id });
    } else {
      setSelectedUser(user.member);
      setTasks(user.tasks);
      setTaskForm({ ...taskForm, leader: user.member.id });
    }
  };

  const handleTaskFormChange = (e) => {
    const { name, value } = e.target;
    setTaskForm({ ...taskForm, [name]: value });
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      const endpoint = activeTab === 'leaders'
        ? '/LeadersTable/create_task/'
        : '/Docladder/create_task/';
      
      const response = await axios.post(
        getApiUrl(endpoint),
        taskForm,
        {
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}` 
          }
        }
      );
      
      setSuccess('Task created successfully!');
      setShowTaskForm(false);
      setTaskForm({ name: '', target: '', points: '', leader: selectedUser.id });
      fetchUsers(); // Refresh the data
      
      // Update local tasks
      setTasks([...tasks, response.data]);
    } catch (err) {
      console.error('Error creating task:', err);
      setError(err.response?.data?.message || 'Failed to create task');
    }
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      const endpoint = activeTab === 'leaders'
        ? `/LeadersTable/update_task/${editingTask.id}/`
        : `/Docladder/update_task/${editingTask.id}/`;
      
      const response = await axios.put(
        getApiUrl(endpoint),
        taskForm,
        {
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}` 
          }
        }
      );
      
      setSuccess('Task updated successfully!');
      setEditingTask(null);
      setShowTaskForm(false);
      setTaskForm({ name: '', target: '', points: '', leader: selectedUser.id });
      fetchUsers(); // Refresh the data
      
      // Update local tasks
      setTasks(tasks.map(t => t.id === editingTask.id ? response.data : t));
    } catch (err) {
      console.error('Error updating task:', err);
      setError(err.response?.data?.message || 'Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    setError('');
    setSuccess('');
    
    try {
      const endpoint = activeTab === 'leaders'
        ? `/LeadersTable/delete_task/${taskId}/`
        : `/Docladder/delete_task/${taskId}/`;
      
      await axios.delete(getApiUrl(endpoint), {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      
      setSuccess('Task deleted successfully!');
      setDeleteConfirmTask(null);
      fetchUsers(); // Refresh the data
      
      // Update local tasks
      setTasks(tasks.filter(t => t.id !== taskId));
    } catch (err) {
      console.error('Error deleting task:', err);
      setError(err.response?.data?.message || 'Failed to delete task');
    }
  };

  const openEditForm = (task) => {
    setEditingTask(task);
    setTaskForm({
      name: task.name,
      target: task.target,
      points: task.points,
      leader: task.leader
    });
    setShowTaskForm(true);
  };

  const openCreateForm = () => {
    setEditingTask(null);
    setTaskForm({ 
      name: '', 
      target: '', 
      points: '', 
      leader: selectedUser?.id || '' 
    });
    setShowTaskForm(true);
  };

  return (
    <div style={{ display: "flex" }}>
      <CustomNavbar />
      <div className="task-management-container" style={{ width: "100%", padding: "20px" }}>
        <h1 className="page-title">Task Management (Super Admin)</h1>
        
        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Tab Switcher */}
        <div className="tab-switcher">
          <button 
            className={`tab-btn ${activeTab === 'leaders' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('leaders');
              setSelectedUser(null);
              setTasks([]);
            }}
          >
            Team Leaders
          </button>
          <button 
            className={`tab-btn ${activeTab === 'docladder' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('docladder');
              setSelectedUser(null);
              setTasks([]);
            }}
          >
            Docladder Members
          </button>
        </div>

        <div className="content-area">
          {/* User Selection Panel */}
          <div className="user-panel">
            <h3>Select User</h3>
            {loading ? (
              <div className="loading">Loading users...</div>
            ) : (
              <div className="user-list">
                {users.map((user, index) => {
                  const userData = activeTab === 'leaders' ? user.leader : user.member;
                  const isSelected = selectedUser?.id === userData.id;
                  
                  return (
                    <div 
                      key={index}
                      className={`user-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleUserSelect(user)}
                    >
                      <div className="user-info">
                        <strong>{userData.name}</strong>
                        <small>@{userData.username}</small>
                      </div>
                      <div className="task-count">
                        <span className="badge">{user.active_tasks_count || user.tasks.length} tasks</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Tasks Panel */}
          <div className="tasks-panel">
            {selectedUser ? (
              <>
                <div className="panel-header">
                  <h3>Tasks for {selectedUser.name}</h3>
                  <button 
                    className="btn btn-primary"
                    onClick={openCreateForm}
                  >
                    + Add New Task
                  </button>
                </div>

                {/* Task Form */}
                {showTaskForm && (
                  <div className="task-form-modal">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h4>{editingTask ? 'Edit Task' : 'Create New Task'}</h4>
                        <button 
                          className="close-btn"
                          onClick={() => {
                            setShowTaskForm(false);
                            setEditingTask(null);
                          }}
                        >
                          ×
                        </button>
                      </div>
                      <form onSubmit={editingTask ? handleUpdateTask : handleCreateTask}>
                        <div className="form-group">
                          <label>Task Name *</label>
                          <input
                            type="text"
                            name="name"
                            value={taskForm.name}
                            onChange={handleTaskFormChange}
                            required
                            placeholder="Enter task name"
                          />
                        </div>
                        <div className="form-group">
                          <label>Target *</label>
                          <input
                            type="number"
                            name="target"
                            value={taskForm.target}
                            onChange={handleTaskFormChange}
                            required
                            step="0.01"
                            placeholder="Enter target value"
                          />
                        </div>
                        <div className="form-group">
                          <label>Points *</label>
                          <input
                            type="number"
                            name="points"
                            value={taskForm.points}
                            onChange={handleTaskFormChange}
                            required
                            step="0.01"
                            placeholder="Enter points"
                          />
                        </div>
                        <div className="form-actions">
                          <button type="submit" className="btn btn-success">
                            {editingTask ? 'Update Task' : 'Create Task'}
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-secondary"
                            onClick={() => {
                              setShowTaskForm(false);
                              setEditingTask(null);
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* Delete Confirmation Modal */}
                {deleteConfirmTask && (
                  <div className="task-form-modal">
                    <div className="modal-content delete-confirm">
                      <h4>⚠️ Confirm Delete</h4>
                      <p>Are you sure you want to delete the task:</p>
                      <p className="task-name-highlight">"{deleteConfirmTask.name}"?</p>
                      <p className="warning-text">This action cannot be undone.</p>
                      <div className="form-actions">
                        <button 
                          className="btn btn-danger"
                          onClick={() => handleDeleteTask(deleteConfirmTask.id)}
                        >
                          Delete
                        </button>
                        <button 
                          className="btn btn-secondary"
                          onClick={() => setDeleteConfirmTask(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tasks List */}
                <div className="tasks-list">
                  {tasks.length === 0 ? (
                    <div className="no-tasks">
                      <p>No tasks assigned yet.</p>
                      <button className="btn btn-primary" onClick={openCreateForm}>
                        Create First Task
                      </button>
                    </div>
                  ) : (
                    <div className="tasks-grid">
                      {tasks.map((task) => (
                        <div key={task.id} className="task-card">
                          <div className="task-header">
                            <h4>{task.name}</h4>
                            {task.is_active !== undefined && (
                              <span className={`status-badge ${task.is_active ? 'active' : 'inactive'}`}>
                                {task.is_active ? 'Active' : 'Inactive'}
                              </span>
                            )}
                          </div>
                          <div className="task-details">
                            <div className="detail-row">
                              <span className="label">Target:</span>
                              <span className="value">{task.target}</span>
                            </div>
                            <div className="detail-row">
                              <span className="label">Points:</span>
                              <span className="value">{task.points}</span>
                            </div>
                            <div className="detail-row">
                              <span className="label">Date Assigned:</span>
                              <span className="value">
                                {new Date(task.dateassigned).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="task-actions">
                            <button 
                              className="btn btn-sm btn-edit"
                              onClick={() => openEditForm(task)}
                            >
                              ✏️ Edit
                            </button>
                            <button 
                              className="btn btn-sm btn-delete"
                              onClick={() => setDeleteConfirmTask(task)}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="no-selection">
                <p>👈 Select a user to view and manage their tasks</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TaskManagement;

